import { forwardRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Calendar, Clock, MapPin } from "lucide-react";
import { submitForm } from "../../api/public.api.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";

const TIME_PREFS = [
  ["morning", "Morning"],
  ["midday", "Midday"],
  ["afternoon", "Afternoon"],
  ["evening", "Evening"],
];

const FORMATS = [
  ["display", "At our display centre"],
  ["video", "Video call"],
  ["phone", "Phone call"],
];

const schema = z.object({
  name: z.string().min(1, "Please enter your name"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().min(6, "Enter a phone number").max(40),
  preferredDate: z.string().optional(),
  preferredTime: z.string().optional(),
  format: z.string().optional(),
  topic: z.string().optional(),
  message: z.string().max(2000).optional(),
});

export default function AppointmentPage() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { format: "display", preferredTime: "morning" },
  });

  const onSubmit = async (values) => {
    try {
      await submitForm({
        formType: "appointment",
        data: values,
        sourcePage: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success("Appointment requested — we'll confirm by email shortly.");
      reset();
    } catch (err) {
      const details = err?.response?.data?.details;
      if (Array.isArray(details) && details.length > 0) {
        toast.error(`${details[0].path}: ${details[0].message}`);
      } else {
        toast.error(err?.response?.data?.message ?? "Submission failed — please try again.");
      }
      console.error("[AppointmentPage]", err?.response?.data ?? err);
    }
  };

  return (
    <>
      <SEO
        title="Request an Appointment"
        description="Arrange a time to talk to our design team — at our display centre, by video, or by phone."
      />

      <PageHero
        eyebrow="Book a visit"
        title="Request an appointment"
        description="A focused chat with our design team. We'll come prepared once you tell us a bit about your project."
      />

      <Container className="py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-16">
          {isSubmitSuccessful && !isSubmitting ? (
            <div className="rounded-2xl border border-success/30 bg-success/10 p-10 text-center">
              <p className="font-display text-2xl text-text-primary">Appointment requested</p>
              <p className="mt-2 text-text-secondary">
                {"We'll confirm the time by email within one business day."}
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="rounded-2xl border border-border-subtle bg-bg-elevated p-8"
              noValidate
            >
              <div className="space-y-4">
                <Field label="Name" required error={errors.name?.message} {...register("name")} />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Email"
                    type="email"
                    required
                    error={errors.email?.message}
                    {...register("email")}
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    required
                    error={errors.phone?.message}
                    {...register("phone")}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Preferred date" type="date" {...register("preferredDate")} />
                  <Select label="Preferred time" {...register("preferredTime")}>
                    {TIME_PREFS.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </Select>
                </div>

                <Select label="Format" {...register("format")}>
                  {FORMATS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>

                <Field
                  label="What would you like to talk about?"
                  placeholder="e.g. Plato 38 on a 12.5m block in Wollert"
                  {...register("topic")}
                />

                <Textarea label="Anything else? (optional)" rows={4} {...register("message")} />

                <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                  {isSubmitting ? "Sending…" : "Request appointment"}
                </button>
                <p className="text-center text-xs text-text-muted">
                  By submitting, you agree to be contacted about your appointment request.
                </p>
              </div>
            </form>
          )}

          <aside className="space-y-4">
            <Card icon={Calendar} title="What to expect">
              Time with one of our designers. No high-pressure pitch — just useful answers.
            </Card>
            <Card icon={Clock} title="Confirmation timing">
              We confirm the exact slot within one business day, by email and SMS.
            </Card>
            <Card icon={MapPin} title="Display centres">
              {"Multiple display homes across Melbourne. We'll suggest the closest one to you."}
            </Card>
          </aside>
        </div>
      </Container>
    </>
  );
}

// forwardRef is *required* — without it, react-hook-form's `register()` ref
// doesn't reach the underlying DOM control, and the form thinks every field
// is empty even after the user fills them in.
const Field = forwardRef(function Field({ label, error, required, ...props }, ref) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </span>
      <input
        ref={ref}
        {...props}
        className={`w-full rounded-md border bg-bg-elevated-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent ${
          error ? "border-danger" : "border-border"
        }`}
      />
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
});

const Select = forwardRef(function Select({ label, children, ...props }, ref) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
      </span>
      <select
        ref={ref}
        {...props}
        className="w-full rounded-md border border-border bg-bg-elevated-2 px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {children}
      </select>
    </label>
  );
});

const Textarea = forwardRef(function Textarea({ label, ...props }, ref) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
      </span>
      <textarea
        ref={ref}
        {...props}
        className="w-full rounded-md border border-border bg-bg-elevated-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
});

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-5">
    <div className="flex items-start gap-3">
      <div className="grid h-9 w-9 place-items-center rounded-md bg-accent/15 text-accent">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="font-display text-base text-text-primary">{title}</p>
        <p className="mt-1 text-sm text-text-secondary">{children}</p>
      </div>
    </div>
  </div>
);
